// src/components/Vid.tsx
import React from 'react';
import styled from 'styled-components';
import preventDefaultEvents from '../hoc/preventDefaultEvents';
import { SECTION_ONE_VIDEO_URL, POSTER_URL } from '@/constants/core';
import { useI18n } from '@/i18n/LanguageProvider';
const VideoWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
`;

const VideoContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border: 2px solid transparent;
  border-image: linear-gradient(
    160deg,
    ${({ theme }) => theme.gradientColors[0]},
    ${({ theme }) => theme.gradientColors[1]}
  );
  border-image-slice: 1;
  overflow: hidden;
  position: relative;

  video {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    display: block;
    background: #000;
    cursor: pointer; /* ⬅ מאפשר קליק לגיטימי */
  }
`;

const PosterButton = styled.button`
  box-sizing: border-box;
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: #000;
  cursor: pointer;
  overflow: hidden;
`;

const PosterImage = styled.img`
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  background: #000;
`;

const PosterPlaceholder = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  aspect-ratio: 2160 / 998;
  background: #000;
`;

const PlayButton = styled.button`
  position: absolute;
  inset: 0;
  margin: auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 0;
  line-height: 0;
  display: grid;
  place-items: center;
  cursor: pointer;
  z-index: 5;
  transition: 0.2s ease;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 16px solid currentColor;
    transform: translateX(2px);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.55);
    transform: scale(1.08);
  }
`;

type VidProps = {
  title?: string;
  posterReady?: boolean;
};

const VideoPlayerBase: React.FC<VidProps> = ({ title, posterReady = true }) => {
  const { i18n } = useI18n();
  const titleAttr = `${title} – וידאו`;
  const aria = `נגן וידאו - ${i18n.BRAND_TITLE_SHORT}`;

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [shouldMountVideo, setShouldMountVideo] = React.useState(false);
  const [hasPosterBeenReady, setHasPosterBeenReady] =
    React.useState(posterReady);

  React.useEffect(() => {
    if (!posterReady) return;

    setHasPosterBeenReady(true);
  }, [posterReady]);

  React.useEffect(() => {
    if (!shouldMountVideo || !videoRef.current) return;

    void videoRef.current.play().catch(() => undefined);
  }, [shouldMountVideo]);

  const playVideo = () => {
    setShouldMountVideo(true);
  };

  return (
    <VideoWrapper>
      <VideoContainer>
        {shouldMountVideo ? (
          <video
            ref={videoRef}
            controls
            preload="none"
            playsInline
            aria-label={aria}
            title={titleAttr}
            poster={POSTER_URL}
            width={2160}
            height={998}
          >
            <source src={SECTION_ONE_VIDEO_URL} type="video/mp4" />
            הדפדפן שלך אינו תומך בוידאו.
            <a href={SECTION_ONE_VIDEO_URL}>פתחו את הסרטון</a>
          </video>
        ) : !hasPosterBeenReady ? (
          <PosterPlaceholder aria-hidden="true" />
        ) : (
          <PosterButton type="button" onClick={playVideo} aria-label={aria}>
            <PosterImage
              src={POSTER_URL}
              alt={titleAttr}
              width={714}
              height={328}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              draggable={false}
            />
            <PlayButton as="span" aria-hidden="true" />
          </PosterButton>
        )}

        <noscript>
          <p>
            לצפייה בוידאו: <a href={SECTION_ONE_VIDEO_URL}>פתחו את הסרטון</a>
          </p>
        </noscript>
      </VideoContainer>
    </VideoWrapper>
  );
};

const Vid = React.memo(VideoPlayerBase);
export default preventDefaultEvents(Vid);
